import { catchError, switchMap, throwError } from 'rxjs';

import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { PlatformAuthOrchestratorService } from '@core/platform-auth/platform-auth-orchestrator.service';
import { PlatformSessionService } from '@core/platform-auth/platform-session.service';

/**
 * Anexa `Authorization: Bearer` a pedidos à API de admin quando existir token na sessão de plataforma.
 */
export const authBearerInterceptor: HttpInterceptorFn = (req, next) => {
  const authOrchestrator = inject(PlatformAuthOrchestratorService);
  const router = inject(Router);
  const session = inject(PlatformSessionService);
  const token = session.accessToken();
  const url = req.url;
  const isAdminApi = url.startsWith('/api/admin');
  const isChangePasswordApi = url.startsWith('/api/auth/change-password');
  const isProtectedApi = isAdminApi || isChangePasswordApi;
  const isRefreshApi = url.startsWith('/api/auth/refresh');
  const isPublicAuthApi = url.startsWith('/api/auth/login') || url.startsWith('/api/auth/logout') || isRefreshApi;
  const withBearer = (tokenValue: string | null) => {
    if (tokenValue == null || tokenValue === '' || !isProtectedApi)
      return req;

    return req.clone({ setHeaders: { Authorization: `Bearer ${tokenValue}` } });
  };

  return next(withBearer(token)).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse))
        return throwError(() => error);

      if (error.status === 403 && isAdminApi) {
        void router.navigateByUrl('/admin/acesso-negado');
        return throwError(() => error);
      }

      if (error.status !== 401 || !isProtectedApi || isPublicAuthApi)
        return throwError(() => error);

      return authOrchestrator.refreshSession().pipe(
        switchMap((ok) => {
          if (!ok) {
            authOrchestrator.redirectToLoginWithReturnUrl(router.url);
            return throwError(() => error);
          }

          const refreshedToken = session.accessToken();
          return next(withBearer(refreshedToken));
        }),
      );
    }),
  );
};
